import PropTypes from 'prop-types';
import { resolveByCountry } from '@codex/helpers';
import { Icon } from '@codex/icons';
import styled from 'styled-components';

import UISettings from '../../settings/UISettings';
import uiLocale from '../../translations/UILocale';
import { getMonthlyPayment, getTotalAmountPayable } from '../../dataConverters/onlineCreditConverter';
import ProductCreditTooltip from './ProductCreditTooltip/ProductCreditTooltip';
import ProductCreditPopup from './ProductCreditPopup/ProductCreditPopup';


const OnlineCreditMessageWrapper = styled.div`
  color: ${({ theme }) => theme.colors.link.hover};
  font-size: 13px;
  text-align: center;
  padding: 0 0 10px 0;
  line-height: 18px;
  cursor: pointer;
  
  @media ${({ theme }) => theme.breakpoints.mobile} {
    font-size: 14px;
  }
`;

const OnlineCreditMessageInfoLabel = styled.span`
  vertical-align: middle;
`;

const OnlineCreditMessageInfoIcon = styled(Icon)`
  width: 14px;
  height: 14px;
  margin-left: 5px;
  vertical-align: middle;

  @media ${({ theme }) => theme.breakpoints.desktop} {
    display: none;
  }
`;

const ProductItemOnlineCreditInfo = ({
  productID, productPriceWithoutCurrencySign, showCreditTooltip, hideCreditTooltip, specialCreditOfferType, gridItemRowLocation,
  deviceType, openedCreditTooltipProductID
}) => {
  
  // constants from settings
  const numberOfMonthlyPayments = UISettings.get('component.onlinecredit.standard_number_monthly_payments');
  const APR = UISettings.get('component.onlinecredit.online_credit_apr');
  const newSaleLimit = UISettings.get('component.onlinecredit.new-sale-limit');
  const isAboveNewSaleLimit = (productPriceWithoutCurrencySign / 100) >= newSaleLimit;
  const monthPricePayment = getMonthlyPayment(productPriceWithoutCurrencySign / 100, APR, numberOfMonthlyPayments).toFixed(2);
  const totalAmountPayable = getTotalAmountPayable(monthPricePayment, numberOfMonthlyPayments).toFixed(2);
  const specialCreditOffersEnabled = UISettings.get('component.onlinecredit.credit_promotional_offers') !== false;

  // currencySign and product Price according to locales
  const currencySign = resolveByCountry('£', '€');
  const formattedProductPrice = (productPriceWithoutCurrencySign / 100).toFixed(2);

  // Labels for UI
  const aboveSaleLimitLinkLabel = uiLocale.getString('component.onlinecredit.listing.credit.available');
  const belowSaleLimitLinkLabel = `${uiLocale.getString('component.onlinecredit.listing.credit.not.available')} ` +
   `${currencySign}${newSaleLimit}`;
  const yourPlanLogoURL = uiLocale.getString('component.onlinecredit.tooltip.your_plan_img_url');
  const monthlyPricePaymentMessageForTooltip = isAboveNewSaleLimit
    ? uiLocale.getFormattedString(
      'component.onlinecredit.tooltip.month_price_payment_message',
      {
        currencySign,
        monthPricePayment
      }
    )
    : '';
  const monthlyPricePaymentMessageForPopup = isAboveNewSaleLimit
    ? uiLocale.getFormattedString(
      'component.onlinecredit.popup.month_price_payment_message',
      {
        currencySign,
        monthPricePayment
      }
    )
    : '';
  const topCreditMessage = isAboveNewSaleLimit
    ? uiLocale.getFormattedString(
      'component.onlinecredit.tooltip.above_limit.top_message',
      {
        numberOfMonthlyPayments,
        APR
      })
    : uiLocale.getFormattedString(
      'component.onlinecredit.tooltip.below_limit.top_message',
      {
        numberOfMonthlyPayments,
        currencySign,
        newSaleLimit,
        APR
      });
  const bottomCreditMessage = isAboveNewSaleLimit
    ? uiLocale.getString(
      'component.onlinecredit.tooltip.above_limit.bottom_message')
    : uiLocale.getFormattedString(
      'component.onlinecredit.tooltip.below_limit.bottom_message',
      {
        currencySign,
        newSaleLimit
      }
    );
  const findOutMoreCreditLink = uiLocale.getString('component.onlinecredit.tooltip.find_out_more_link');
  const representativeExampleHeader = isAboveNewSaleLimit
    ? uiLocale.getString('component.onlinecredit.representative_example_header') : '';
  const representativeExampleMessage = isAboveNewSaleLimit
    ? uiLocale.getFormattedString(
      'component.onlinecredit.representative_example_message',
      {
        currencySign,
        productPrice: formattedProductPrice,
        numberOfMonthlyPayments,
        monthPricePayment,
        totalAmountPayable,
        APR
      }
    ) : '';
  const representativeExampleNote = isAboveNewSaleLimit
    ? uiLocale.getString('component.onlinecredit.representative_example_note') : '';
  const creditAccountFaqsLink = isAboveNewSaleLimit
    ? uiLocale.getString('component.onlinecredit.credit_account_faq_link') : '';
  const creditAccountInformationLink = isAboveNewSaleLimit
    ? uiLocale.getString('component.onlinecredit.credit_account_information_link') : '';

  let specialCreditOfferMessage = '';
  if (specialCreditOffersEnabled && specialCreditOfferType) {
    specialCreditOfferMessage = specialCreditOfferType === 'BNPL'
      ? uiLocale.getString('component.onlinecredit.tooltip.buy_now_pay_later_message')
      // special credit offer is not BNPL type so it's IFC type
      : uiLocale.getString('component.onlinecredit.tooltip.interest_credit_free_message');
  }


  return (
    <>
      <OnlineCreditMessageWrapper
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          showCreditTooltip(productID);
        }}
      >
        <OnlineCreditMessageInfoLabel>
          {isAboveNewSaleLimit ? aboveSaleLimitLinkLabel : belowSaleLimitLinkLabel}
        </OnlineCreditMessageInfoLabel>
        <OnlineCreditMessageInfoIcon icon={'Info'} />
      </OnlineCreditMessageWrapper>
      {(productID === openedCreditTooltipProductID) &&
        (deviceType === 'desktop' || deviceType === 'wide' || deviceType === 'tablet') && (
        <ProductCreditTooltip
          topLogoUrl={yourPlanLogoURL}
          monthlyPricePaymentMessage={monthlyPricePaymentMessageForTooltip}
          topCreditMessage={topCreditMessage}
          bottomCreditMessage={bottomCreditMessage}
          specialCreditOfferMessage={specialCreditOfferMessage}
          findOutMoreCreditLink={findOutMoreCreditLink}
          deviceType={deviceType}
          onCloseButtonClick={hideCreditTooltip}
          onOutsidePopupClick={hideCreditTooltip}
          gridItemRowLocation={gridItemRowLocation}
        />
      )}
      {productID === openedCreditTooltipProductID && deviceType === 'mobile' && (
        <ProductCreditPopup
          onPopupCloseAction={hideCreditTooltip}
          popupTitle={isAboveNewSaleLimit ? aboveSaleLimitLinkLabel : belowSaleLimitLinkLabel}
          topLogoUrl={yourPlanLogoURL}
          monthlyPricePaymentMessage={monthlyPricePaymentMessageForPopup}
          topCreditMessage={topCreditMessage}
          bottomCreditMessage={bottomCreditMessage}
          representativeExampleHeader={representativeExampleHeader}
          representativeExampleMessage={representativeExampleMessage}
          representativeExampleNote={representativeExampleNote}
          creditAccountFaqsLink={creditAccountFaqsLink}
          creditAccountInformationLink={creditAccountInformationLink}
          findOutMoreCreditLink={findOutMoreCreditLink}
          specialCreditOfferMessage={specialCreditOfferMessage}
        />
      )}
    </>
  );

};

ProductItemOnlineCreditInfo.propTypes = {
  productID: PropTypes.string.isRequired,
  productPriceWithoutCurrencySign: PropTypes.number.isRequired,
  showCreditTooltip: PropTypes.func.isRequired,
  hideCreditTooltip: PropTypes.func.isRequired,
  specialCreditOfferType: PropTypes.oneOf(['BNPL', 'IFC']),
  gridItemRowLocation: PropTypes.oneOf(['left', 'middle', 'right']),
  deviceType: PropTypes.oneOf(['wide', 'desktop', 'tablet', 'mobile', 'SSR']),
  openedCreditTooltipProductID: PropTypes.string
};

ProductItemOnlineCreditInfo.defaultProps = {
  specialCreditOfferType: undefined,
  gridItemRowLocation: undefined,
  deviceType: 'SSR',
  openedCreditTooltipProductID: null
};


export default ProductItemOnlineCreditInfo;
